/** @module components/AddressSearchGoogle */

import {
  FormEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useGooglePlacesAutocomplete } from '../hooks/use-google-places-autocomplete';
import { getDefaultLabels } from '../constants';
import type { AddressSearchGoogleProps, AddressSuggestion } from '../types';
import './AddressSearchGoogle.css';

export const AddressSearchGoogle = ({
  address,
  onAddress,
  onCoordinates,
  onQuotaError,
  languageCode = 'en',
  labels: labelOverrides,
  locationRestriction,
  locationBias,
  includedPrimaryTypes,
  regionCode,
  className,
}: AddressSearchGoogleProps) => {
  const labels = useMemo(
    () => ({ ...getDefaultLabels(languageCode), ...labelOverrides }),
    [languageCode, labelOverrides],
  );

  const inputRef = useRef<HTMLInputElement | null>(null);
  const suggestionsListRef = useRef<HTMLUListElement | null>(null);

  const [inputValue, setInputValue] = useState(address);
  const [activeIndex, setActiveIndex] = useState(0);
  const [suggestionsVisible, setSuggestionsVisible] = useState(false);
  const suggestionsVisibleRef = useRef(false);

  const {
    suggestions,
    isLoading,
    error,
    status,
    search,
    selectSuggestion,
  } = useGooglePlacesAutocomplete({
    onAddress,
    onCoordinates,
    onQuotaError,
    languageCode,
    locationRestriction,
    locationBias,
    includedPrimaryTypes,
    regionCode,
    labels: labelOverrides,
  });

  // Keep input in sync with external address prop.
  useEffect(() => {
    setInputValue(address);
  }, [address]);

  // Show/hide dropdown when suggestions change.
  useEffect(() => {
    setSuggestionsVisible(suggestions.length > 0);
    setActiveIndex(0);
  }, [suggestions]);

  // Keep the ref in sync for the click-away handler.
  useEffect(() => {
    suggestionsVisibleRef.current = suggestionsVisible;
  }, [suggestionsVisible]);

  // Click-away: single listener registration.
  useEffect(() => {
    const handleClickAway = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!suggestionsVisibleRef.current) return;
      if (inputRef.current?.contains(target)) return;
      if (suggestionsListRef.current?.contains(target)) return;
      setSuggestionsVisible(false);
    };

    document.addEventListener('mousedown', handleClickAway);
    return () => document.removeEventListener('mousedown', handleClickAway);
  }, []);

  const handleChange = (event: FormEvent<HTMLInputElement>): void => {
    const { value } = event.currentTarget;
    setInputValue(value);
    onAddress(value);
    search(value);
  };

  const handleSuggestionSelect = useCallback(async (suggestion: AddressSuggestion) => {
    setInputValue(suggestion.fullText);
    await selectSuggestion(suggestion);
  }, [selectSuggestion]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!suggestionsVisible || suggestions.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((current) => (current + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) => (current - 1 + suggestions.length) % suggestions.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const suggestion = suggestions[activeIndex];
      if (suggestion) handleSuggestionSelect(suggestion);
    } else if (event.key === 'Escape') {
      setSuggestionsVisible(false);
    }
  };

  const renderError = (err: ReactNode) => (
    <div className="asg-error-panel">
      <div className="asg-error-heading">{labels.warning}</div>
      <div>{err}</div>
    </div>
  );

  return (
    <>
      <div className={`asg-wrapper${className ? ` ${className}` : ''}`}>
        <span>{labels.question}</span>
        <input
          ref={inputRef}
          id="address"
          className="asg-input"
          type="search"
          autoComplete="off"
          placeholder={labels.placeholder}
          value={inputValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length) setSuggestionsVisible(true);
          }}
          maxLength={150}
          disabled={status === 'loading'}
          role="combobox"
          aria-controls="address-suggestions-listbox"
          aria-autocomplete="list"
          aria-expanded={suggestionsVisible}
          aria-activedescendant={
            suggestionsVisible && suggestions[activeIndex]
              ? `suggestion-${suggestions[activeIndex].id}`
              : undefined
          }
        />
        {suggestionsVisible && suggestions.length > 0 && (
          <ul
            id="address-suggestions-listbox"
            ref={suggestionsListRef}
            className="asg-suggestions"
            role="listbox"
          >
            {suggestions.map((suggestion, index) => (
              <li
                key={suggestion.id}
                id={`suggestion-${suggestion.id}`}
                className={
                  index === activeIndex
                    ? 'asg-suggestion-item asg-suggestion-item-active'
                    : 'asg-suggestion-item'
                }
                role="option"
                aria-selected={index === activeIndex}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseDown={(event) => {
                  event.preventDefault();
                  handleSuggestionSelect(suggestion);
                }}
              >
                <span>{suggestion.primaryText}</span>
                {suggestion.secondaryText && (
                  <small className="asg-suggestion-secondary">
                    {suggestion.secondaryText}
                  </small>
                )}
              </li>
            ))}
            {isLoading && (
              <li className="asg-loading">{labels.searching}</li>
            )}
          </ul>
        )}
      </div>
      {error && renderError(error)}
    </>
  );
};
